import { Routes } from '@angular/router';
import { DocumentTypeEnum } from '@core/enums/document/document-type.enum';
import { AuthRedirectGuard } from '@core/guards/auth-redirect.guard';
import { AuthGuard } from '@core/guards/auth.guard';
import { AccessControlFeatureKey } from '@shared/constants/features.const';
import { featureAccessMatchGuard } from '@core/guards/feature-access-match.guard';
import { PolicyGuard } from '@core/guards/policy.guard';
import { getPolicyResolver } from '@core/pages/public/policy/get-policy.resolver';
import { PolicyToPageMapperService } from '@core/pages/public/policy/mappers/accept-policy.mapper.service';
import { AcceptPolicyUseCaseService } from '@core/pages/public/policy/use-cases/accept-policy.use-case.service';
import { GetPolicyUseCaseService } from '@core/pages/public/policy/use-cases/get-policy.use-case.service';
import { REPORTS_FEATURES } from './modules/reports/reports.features';
import { PARAMETERS_LIMITS_COMMON } from './modules/settings/pages/parameters-and-limits/parameters-and-limits.features';
import { PERIMETERS_FEATURE } from './modules/perimeters/perimeters.features';
import { authMatchGuard } from '@core/guards/auth-match.guard';
import { PROTOTYPES_APP_ROUTES } from './modules/prototypes/prototypes.entry';

export const routes: Routes = [
  {
    path: 'ui/ref',
    loadChildren: () => import('./ui/pages/ui-ref/ui-ref.route').then(m => m.uiRefRoute),
    canActivate: [AuthGuard, PolicyGuard],
  },
  {
    path: 'login',
    loadChildren: () => import('./modules/auth/auth.routes').then(m => m.authRoutes),
    data: { hideMenus: 'true' },
    canActivate: [AuthRedirectGuard],
  },
  {
    path: '',
    redirectTo: 'painel-frotista',
    pathMatch: 'full',
  },
  {
    path: 'localizacao',
    loadChildren: () =>
      import('./modules/location/pages/location.routes').then(m => m.locationRoutes),
    canActivate: [AuthGuard, PolicyGuard],
    canMatch: [authMatchGuard, featureAccessMatchGuard],
    data: { features: [AccessControlFeatureKey.Fts] },
  },
  {
    path: 'onboard',
    canActivate: [AuthGuard, PolicyGuard],
    data: {
      features: [AccessControlFeatureKey.Onboard],
    },
    canMatch: [authMatchGuard, featureAccessMatchGuard],
    children: [
      {
        path: 'pagina-inicial',
        loadComponent: () =>
          import('./modules/onboard/pages/home-page/home-page.component').then(
            m => m.HomePageComponent
          ),
        data: { features: [AccessControlFeatureKey.Onboard] },
      },
      {
        path: 'painel-bvdr2',
        loadComponent: () =>
          import('./modules/onboard/pages/bvdr2-dashboard/bvdr2-dashboard.component').then(
            m => m.Bvdr2DashboardComponent
          ),
        data: {
          features: [AccessControlFeatureKey.ManagementPanel],
        },
      },
      {
        path: 'arquivos-vdr',
        loadChildren: () =>
          import('./modules/onboard/vdr-files/vdr-files.routes').then(m => m.vdrFilesRoutes),
        data: { features: [AccessControlFeatureKey.Upload, AccessControlFeatureKey.Storage] },
      },
      {
        path: 'relatorio24h',
        loadChildren: () =>
          import('./modules/onboard/pages/upload-tachograph/upload-tachograph.routes').then(
            m => m.uploadTachograph
          ),
        data: { features: [AccessControlFeatureKey.TwentyFourHours] },
      },
    ],
  },
  {
    path: 'perimetros',
    canActivate: [AuthGuard, PolicyGuard],
    canActivateChild: [AuthGuard, PolicyGuard],
    data: { features: PERIMETERS_FEATURE },
    canMatch: [authMatchGuard, featureAccessMatchGuard],
    children: [
      {
        path: 'categorias',
        loadChildren: () =>
          import('./modules/perimeters/pages/categories/categorias.route').then(
            m => m.categoriasRoute
          ),
        data: { features: PERIMETERS_FEATURE },
      },
      {
        path: 'ponto-de-referencia',
        loadChildren: () =>
          import('./modules/perimeters/pages/reference-point/pages/reference-point.routes').then(
            m => m.referencePointsRoutes
          ),
        data: { features: PERIMETERS_FEATURE },
      },
      {
        path: 'cercas-e-rotas',
        loadChildren: () =>
          import('./modules/perimeters/pages/fences-and-routes/fences-and-routes.routes').then(
            m => m.fencesRoutesRoute
          ),
        data: { features: PERIMETERS_FEATURE },
      },
      {
        path: 'telemetria',
        loadChildren: () =>
          import('./modules/perimeters/pages/telemetry/telemetry.routes').then(
            m => m.telemetryRoutes
          ),
        data: { features: PERIMETERS_FEATURE },
      },
      {
        path: 'telemetria-gerenciamento',
        loadChildren: () =>
          import('./modules/perimeters/telemetry-manage/telemetry-manage.routes').then(
            m => m.telemetryManageRoutes
          ),
        data: { features: PERIMETERS_FEATURE },
      },
    ],
  },
  {
    path: 'ultimos-registros',
    loadChildren: () =>
      import('./modules/last-records/last-records.routes').then(m => m.lastRecordsRoutes),
    canActivate: [AuthGuard, PolicyGuard],
    canMatch: [authMatchGuard, featureAccessMatchGuard],
    data: { features: [AccessControlFeatureKey.Fts] },
  },
  {
    path: 'rotas',
    loadChildren: () => import('./modules/routes/routes.routes').then(m => m.routesRoutes),
    canMatch: [authMatchGuard, featureAccessMatchGuard],
    data: { features: [AccessControlFeatureKey.FtsRoute] },
    canActivate: [AuthGuard, PolicyGuard],
  },
  {
    path: 'ocorrencias',
    loadChildren: () =>
      import('./modules/occurrences/occurrences.routes').then(m => m.occurrencesRoutes),
    data: { features: [AccessControlFeatureKey.FtsIssues] },
    canActivate: [AuthGuard, PolicyGuard],
    canMatch: [authMatchGuard, featureAccessMatchGuard],
  },
  {
    path: 'gestao-risco',
    loadChildren: () =>
      import('./modules/risk-management/risk-management.routes').then(m => m.riskManagementRoutes),
    canActivate: [AuthGuard, PolicyGuard],
    canMatch: [authMatchGuard, featureAccessMatchGuard],
    data: {
      coreBackgroundColor: '#F3F3F3',
      features: [AccessControlFeatureKey.FtsRiskMgmV2],
    },
  },
  {
    path: 'bloqueio-e-sirene',
    loadChildren: () =>
      import('./modules/block-siren/block-siren.routes').then(m => m.blockSirenRoutes),
    canActivate: [AuthGuard, PolicyGuard],
    canMatch: [authMatchGuard, featureAccessMatchGuard],
    data: {
      coreBackgroundColor: '#F3F3F3',
      features: [AccessControlFeatureKey.FtsBlockSiren, AccessControlFeatureKey.FtsSendSiren],
    },
  },
  {
    path: 'configuracoes-remotas',
    loadChildren: () =>
      import('./modules/remote-configuration/remote-configuration.routes').then(
        m => m.remoteConfigurationRoutes
      ),
    canActivate: [AuthGuard, PolicyGuard],
    canMatch: [authMatchGuard, featureAccessMatchGuard],
    data: {
      coreBackgroundColor: '#F3F3F3',
      features: [
        AccessControlFeatureKey.FtsDeviceCmd,
        AccessControlFeatureKey.FtsDeviceCmdOdometer,
        AccessControlFeatureKey.FtsDeviceCmdHorimeter,
        AccessControlFeatureKey.FtsDeviceCmdOperation,
      ],
    },
  },
  {
    path: 'ranking',
    loadChildren: () => import('./modules/ranking/ranking.routes').then(m => m.rankingRoutes),
    canActivate: [AuthGuard, PolicyGuard],
    canActivateChild: [AuthGuard, PolicyGuard],
    canMatch: [authMatchGuard, featureAccessMatchGuard],
    data: { features: [AccessControlFeatureKey.FtsDriverRanking] },
  },
  {
    path: 'jornadas',
    loadChildren: () => import('./modules/workday/workday.routes').then(m => m.workdayRoutes),
    canActivate: [AuthGuard, PolicyGuard],
    canMatch: [authMatchGuard, featureAccessMatchGuard],
    data: { features: [AccessControlFeatureKey.WorkedHours] },
  },
  {
    path: 'painel-frotista',
    loadChildren: () =>
      import('./modules/fleet-panel/fleet-panel.routes').then(m => m.fleetPanelRoutes),
    canActivate: [AuthGuard, PolicyGuard],
    canMatch: [authMatchGuard, featureAccessMatchGuard],
    data: { features: [AccessControlFeatureKey.Fts] },
  },
  {
    path: 'relatorios',
    loadChildren: () => import('./modules/reports/reports.routes').then(m => m.reportsRoutes),
    canActivate: [AuthGuard, PolicyGuard],
    canActivateChild: [AuthGuard, PolicyGuard],
    canMatch: [authMatchGuard, featureAccessMatchGuard],
    data: {
      features: REPORTS_FEATURES,
    },
  },
  {
    path: 'servicos',
    loadChildren: () => import('./modules/services/services.routes').then(m => m.servicesRoutes),
    canActivate: [AuthGuard, PolicyGuard],
    canActivateChild: [AuthGuard, PolicyGuard],
    canMatch: [authMatchGuard, featureAccessMatchGuard],
    data: { features: [AccessControlFeatureKey.AccountManagementServiceRequests] },
  },
  {
    path: 'agendamento',
    loadChildren: () =>
      import('./modules/reports/pages/report-scheduling/report-scheduling.routes').then(
        m => m.reportSchedulingRoutes
      ),
    canActivate: [AuthGuard, PolicyGuard],
    canMatch: [authMatchGuard, featureAccessMatchGuard],
    data: { features: [AccessControlFeatureKey.FtsReportSchedule] },
  },
  {
    path: 'configuracoes',
    data: {
      features: [
        AccessControlFeatureKey.AccountManagement,
        AccessControlFeatureKey.UserManagement,
        AccessControlFeatureKey.FtsRoute,
        AccessControlFeatureKey.FtsOverspeed,
        AccessControlFeatureKey.FtsSpeedRpm,
        AccessControlFeatureKey.FtsKm,
        AccessControlFeatureKey.FtsReportsInfractions,
        AccessControlFeatureKey.FtsIssues,
        AccessControlFeatureKey.FtsPerimeters,
        AccessControlFeatureKey.WorkedHours,
        AccessControlFeatureKey.Uploads,
      ],
    },
    children: [
      {
        path: 'parametros-e-limites',
        loadChildren: () =>
          import(
            './modules/settings/pages/parameters-and-limits/parameters-and-limitss.routes'
          ).then(m => m.parametersAndLimitsRoutes),
        canActivate: [PolicyGuard],
        canMatch: [authMatchGuard, featureAccessMatchGuard],
        data: {
          features: PARAMETERS_LIMITS_COMMON,
        },
      },
      {
        path: 'configuracoes-de-hora',
        loadChildren: () =>
          import('./modules/settings/pages/time-settings/time-settings.routes').then(
            m => m.timeSettingsRoutes
          ),
        canActivate: [PolicyGuard],
      },
      {
        path: 'minha-conta',
        loadChildren: () =>
          import('./modules/settings/pages/my-account/my-account.routes').then(
            m => m.myAccountRoutes
          ),
        canActivate: [PolicyGuard],
        canMatch: [authMatchGuard, featureAccessMatchGuard],
        data: { features: [AccessControlFeatureKey.AccountManagement] },
      },
      {
        path: 'upload-dados-veiculo',
        loadChildren: () =>
          import('./modules/settings/pages/vehicle-data-upload/vehicle-data-upload.routes').then(
            m => m.vehicleDataUploadRoutes
          ),
        canActivate: [PolicyGuard],
      },
      {
        path: 'termos-de-uso',
        loadComponent: () =>
          import('./core/pages/public/policy/policy.component').then(m => m.PolicyComponent),
        data: {
          policyType: DocumentTypeEnum.TERMS_OF_USE,
          shouldShowAcceptButton: true,
          coreBackgroundColor: '#f9f9fe',
        },
        resolve: {
          policy: getPolicyResolver,
        },
        providers: [GetPolicyUseCaseService, PolicyToPageMapperService, AcceptPolicyUseCaseService],
      },
      {
        path: 'perfis-de-acesso',
        loadChildren: () =>
          import('./modules/settings/pages/access-profile/access-profile.routes').then(
            m => m.accessProfileRoutes
          ),
        canActivate: [PolicyGuard],
        canMatch: [authMatchGuard, featureAccessMatchGuard],
        data: { features: [AccessControlFeatureKey.AccountManagement] },
      },
      {
        path: 'usuarios',
        loadChildren: () =>
          import('./modules/settings/pages/users-management/users-management.routes').then(
            m => m.usersManagementRoutes
          ),
        canMatch: [authMatchGuard, featureAccessMatchGuard],
        data: { features: [AccessControlFeatureKey.UserManagement] },
      },
    ],
  },
  {
    path: 'gerenciamento-frota',
    canActivate: [AuthGuard, PolicyGuard],
    canActivateChild: [AuthGuard, PolicyGuard],
    canMatch: [authMatchGuard, featureAccessMatchGuard],
    data: { features: [AccessControlFeatureKey.FleetManagement] },
    children: [
      {
        path: 'filiais',
        loadChildren: () =>
          import(
            './modules/fleet-management/pages/branch-management/branch-management.routes'
          ).then(m => m.branchManagementRoutes),
        data: { features: [AccessControlFeatureKey.FleetManagement] },
      },
      {
        path: 'garagens',
        loadChildren: () =>
          import(
            './modules/fleet-management/pages/garage-management/garage-management.routes'
          ).then(m => m.garageManagementRoutes),
        data: { features: [AccessControlFeatureKey.FleetManagement] },
      },
      {
        path: 'motoristas',
        loadChildren: () =>
          import(
            './modules/fleet-management/pages/driver-management/driver-management.routes'
          ).then(m => m.driverManagementRoutes),
        data: { features: [AccessControlFeatureKey.FleetManagement] },
      },
      {
        path: 'identificadores',
        loadChildren: () =>
          import(
            './modules/fleet-management/pages/identifier-management/identifier-management.routes'
          ).then(m => m.identifierManagementRoutes),
        data: { features: [AccessControlFeatureKey.Fts] },
        canMatch: [authMatchGuard, featureAccessMatchGuard],
      },
      {
        path: 'veiculos',
        loadChildren: () =>
          import(
            './modules/fleet-management/pages/vehicle-management/vehicle-management.routes'
          ).then(m => m.vehicleManagementRoutes),
        data: { features: [AccessControlFeatureKey.FleetManagement] },
      },
    ],
  },
  {
    path: 'manutencoes',
    canActivate: [AuthGuard, PolicyGuard],
    canActivateChild: [AuthGuard, PolicyGuard],
    canMatch: [authMatchGuard, featureAccessMatchGuard],
    data: { features: [AccessControlFeatureKey.FleetManagementScheduledServices] },
    children: [
      {
        path: '',
        loadChildren: () =>
          import('./modules/maintenance/maintenance.routes').then(m => m.maintenanceRoutes),
        data: { features: [AccessControlFeatureKey.FleetManagementScheduledServices] },
      },
    ],
  },
  ...PROTOTYPES_APP_ROUTES,
  {
    path: 'extrair-dados/:jobId',
    loadComponent: () =>
      import('./ui/components/data-export/data-export.component').then(m => m.DataExportComponent),
    data: { basicLayout: true, features: [AccessControlFeatureKey.FtsDataAExtraction] },
    canActivate: [AuthGuard, PolicyGuard],
    canMatch: [authMatchGuard, featureAccessMatchGuard],
  },
  {
    path: 'publico',
    loadChildren: () => import('./core/pages/public/public.routes').then(m => m.publicRoutes),
  },
  {
    path: 'sem-acesso',
    loadComponent: () =>
      import('./core/pages/no-access/no-access.component').then(m => m.NoAccessComponent),
    canActivate: [AuthGuard, PolicyGuard],
  },
  {
    path: '**',
    redirectTo: 'login',
  },
];
